import { useNavigate } from "react-router-dom";
import { X, ThumbsDown, ThumbsUp, Save, FileDown } from "lucide-react";
import { toast } from "react-toastify";

import CustomButton from "@/components/desktop/button/CustomButton";
import { hasPermission } from "@/services/Utils";
import { useAppOrderCreate, useAppOrderCodeCreate } from "@/hooks/planning/application-order/applicationOrders";

import classes from "./createOrderApplication.module.scss";

const CreateAppOrderButton = ({
	params,
	base,
	data,
	setData,
	save,
	approve,
	reject,
	downloadPDF,
	option,
	savedRef,
	isSaving,
	loadApprove,
	loadReject,
	isDownloadingPDF,
}) => {
	const navigate = useNavigate();

	const { data: response } = useAppOrderCreate(params);
	const { isLoading } = useAppOrderCodeCreate(data.estate_parent_id, data.app_order_id);

	const { permissions, app_order_types_customer } = response ? response : {};

	const isViewMode = option === "ver";
	const isPendingState = data?.application_orders_state_id && [1].includes(Number(data.application_orders_state_id));
	const isDraftState = data?.application_orders_state_id && Number(data.application_orders_state_id) === 6;
	const canApprove = !isViewMode && isPendingState && data?.app_order_id && hasPermission("approve", permissions);
	const canEdit = !isViewMode && (hasPermission("edit", permissions) || hasPermission("create", permissions));
	const canDownloadPDF = data?.app_order_id && (canEdit || isViewMode) && !isDraftState;

	const validSave = () => {
		if (!data.application_order_type_id) {
			toast.error("Debe seleccionar tipo de orden de aplicación para continuar.");
			return false;
		}

		if (app_order_types_customer && app_order_types_customer.length > 0 && !data.application_order_type_id_customer) {
			toast.error("Debe seleccionar tipo de orden de aplicación para continuar.");
			return false;
		}

		if (!data.code) {
			toast.error("Debe ingresar un código para continuar.");
			return false;
		}

		if (!data.business_id) {
			toast.error("Debe seleccionar razón social para continuar.");
			return false;
		}

		if (Number(data.application_order_type_id) !== 2 && !data.machineries.length > 0) {
			toast.error("Debe seleccionar al menos una maquinaria para continuar.");
			return false;
		}

		if (!data.items.length > 0) {
			toast.error("Debe seleccionar al menos un producto para continuar.");
			return false;
		}

		const totalArea = data.business_centers.reduce((sum, item) => {
			const parsed = Number(String(item.area_select).replace(",", "."));
			return sum + (isNaN(parsed) ? 0 : parsed);
		}, 0);

		let valid_item = true;
		let message = "";
		data.items.forEach((item, _i) => {
			if (!item.application_orders_objetives_id) {
				message = "Debe seleccionar un objetivo en todos los items";
				valid_item = false;
				return;
			}

			if (!item.product_id) {
				message = "Debe seleccionar un producto en todos los items";
				valid_item = false;
				return;
			}

			if (!item.quantity && totalArea === 0 && Number(data.application_order_type_id) !== 3) {
				message = "Los centros de costo seleccionados no tienen hectáreas asignadas";
				valid_item = false;
				return;
			}

			if (Number(data.application_order_type_id) !== 3) {
				if (!item.quantity) {
					message = "Cada producto debe tener un gasto total";
					valid_item = false;
					return;
				}
			}

			if (Number(data.application_order_type_id) === 3) {
				if (!item.dose_machinery) {
					message = "Cada producto debe tener una dosis por maquinaria";
					valid_item = false;
					return;
				}
			}
		});

		if (!valid_item) {
			toast.error(message);
			return false;
		}

		return true;
	};

	const validApprove = () => {
		const totalArea = data.business_centers.reduce((sum, item) => {
			const parsed = Number(String(item.area_select).replace(",", "."));
			return sum + (isNaN(parsed) ? 0 : parsed);
		}, 0);

		for (const item of data.items) {
			if (!item.quantity && totalArea === 0 && Number(data.application_order_type_id) !== 3) {
				toast.error("Los centros de costo seleccionados no tienen hectáreas asignadas");
				return false;
			}

			if (Number(data.application_order_type_id) !== 3) {
				if (!item.quantity) {
					toast.error("Cada producto debe tener un gasto total antes de aprobar");
					return false;
				}
			}

			if (Number(data.application_order_type_id) === 3) {
				if (!item.dose_machinery) {
					toast.error("Cada producto debe tener una dosis por maquinaria antes de aprobar");
					return false;
				}
			}
		}

		return true;
	};

	const handleSave = () => {
		if (validSave()) {
			save(
				{ params: data, base: base },
				{
					onSuccess: (_response) => {
						savedRef.current = true;
						toast.success("Orden de Aplicación guardada exitosamente.");
						navigate("/ordenes_aplicacion");
					},
				},
			);
		}
	};

	const handleSaveDraft = () => {
		const draftValid = () => {
			if (!data.application_order_type_id) {
				toast.error("Debe seleccionar tipo de orden de aplicación para continuar.");
				return false;
			}
			if (!data.code) {
				toast.error("Debe ingresar un código para continuar.");
				return false;
			}
			if (!data.estate_parent_id) {
				toast.error("Debe ingresar campo para continuar.");
				return false;
			}
			if (!data.estate_id) {
				toast.error("Debe ingresar bodega para continuar.");
				return false;
			}
			return true;
		};

		if (draftValid()) {
			data.draft = true;
			save(
				{ params: data, base: base },
				{
					onSuccess: () => {
						toast.success("Borrador guardado exitosamente.");
						setData((prevData) => ({
							...prevData,
							app_order_id: 1,
						}));
						navigate("/ordenes_aplicacion");
					},
				},
			);
		}
	};

	const handleReject = () => {
		reject(
			{
				code: data.code,
				base,
				element: data,
			},
			{
				onSuccess: (response) => {
					toast.success(`OA rechazada correctamente.`);

					if (response.order) {
						setData(response.order);
					}
				},
			},
		);
	};

	const handleApprove = () => {
		if (!validApprove()) {
			return;
		}

		approve(
			{
				code: data.code,
				base,
				element: data,
			},
			{
				onSuccess: (response) => {
					toast.success(`OA aprobada correctamente.`);

					if (response.order) {
						setData(response.order);
					}
				},
			},
		);
	};

	const handleDownloadPDF = () => {
		downloadPDF(
			{
				code: data.code,
			},
			{
				onSuccess: (response) => {
					toast.success(`PDF OA ${data.code} descargado correctamente.`);

					if (response.order) {
						setData(response.order);
					}
				},
			},
		);
	};

	return (
		<div className={classes.container}>
			<div className={classes.buttons}>
				<CustomButton
					variant="danger"
					size="md"
					startIcon={<X size={16} />}
					onClick={() => (navigate(-1) ? navigate(-1) : navigate("/ordenes_aplicacion"))}
					disabled={isLoading}
				>
					CERRAR
				</CustomButton>

				<div className={classes.buttonGroup}>
					{canApprove && (
						<CustomButton
							variant="warning"
							size="md"
							startIcon={<ThumbsDown size={16} />}
							onClick={handleReject}
							disabled={isLoading}
							loading={loadReject}
						>
							RECHAZAR
						</CustomButton>
					)}

					{canApprove && (
						<CustomButton
							variant="success"
							size="md"
							startIcon={<ThumbsUp size={16} />}
							onClick={handleApprove}
							disabled={isLoading}
							loading={loadApprove}
						>
							APROBAR
						</CustomButton>
					)}
				</div>

				<div className={classes.buttonGroup}>
					{canDownloadPDF && (
						<CustomButton
							variant="info"
							size="md"
							startIcon={<FileDown size={16} />}
							onClick={handleDownloadPDF}
							disabled={isLoading}
							loading={isDownloadingPDF}
						>
							PDF
						</CustomButton>
					)}

					{(isDraftState || data?.app_order_id === "") && canEdit && (
						<CustomButton
							variant="neutral"
							size="md"
							startIcon={<Save size={16} />}
							onClick={handleSaveDraft}
							disabled={isLoading}
							loading={isSaving}
						>
							GUARDAR BORRADOR
						</CustomButton>
					)}

					{data && canEdit && (
						<CustomButton variant="primary" size="md" startIcon={<Save size={16} />} onClick={handleSave} disabled={isLoading} loading={isSaving}>
							GUARDAR
						</CustomButton>
					)}
				</div>
			</div>
		</div>
	);
};

export default CreateAppOrderButton;
